import React, { FC, useEffect } from 'react';

import { Button, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';

import FilterIcon from '~/assets/icons/FilterIcon';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import { useDiscoverContext } from '~/context/DiscoverContext';
import useSERPNavigation from '~/hooks/useSERPNavigation';
import useStats from '~/hooks/useStats';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import ViewSwitcher from '~/pages/DiscoverPage/components/ViewSwitcher/ViewSwitcher';
import { HITS_TYPES } from '~/pages/const';
import { MIDDLE_MARGIN_PX, SKELETON_TEXT_HEIGHT_TINY_PX, SMALL_MARGIN_PX } from '~/theme';

interface SearchResultsProps {
  withToggle?: boolean;
  resultsFunc: (count: number) => string | JSX.Element;
  setPage?: (page: number) => void;
  setItemsCount?: (itemsCount: number) => void;
  loading?: boolean;
}

export const SearchResults: FC<SearchResultsProps> = ({
  setItemsCount,
  setPage,
  resultsFunc,
  withToggle = false,
  loading = false,
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const { setOpenFiltersPanel, searchState, setTempSearchState } = useDiscoverContext();
  const { nbHits, page } = useStats();
  const searchType = useSERPNavigation();

  useEffect(() => {
    setPage && setPage(page);
  }, [page, setPage]);

  useEffect(() => {
    setItemsCount && setItemsCount(nbHits);
  }, [nbHits, setItemsCount]);

  return (
    <SearchResult>
      {loading ? (
        <LoadingResultsCount xs={SKELETON_TEXT_HEIGHT_TINY_PX} />
      ) : (
        <Text data-testid="resultsAmount">
          {nbHits} {resultsFunc(nbHits)}
        </Text>
      )}
      <ButtonsWrapper>
        {searchType !== HITS_TYPES.COURSE && (
          <FilterBtn
            onClick={() => {
              setTempSearchState(searchState);
              setOpenFiltersPanel(true);
            }}
            withToggle={withToggle}
          >
            <StyledFilterIcon color={theme.palette.common.gray} />
            {t('page.discover.filters')}
          </FilterBtn>
        )}
        {withToggle && <ViewSwitcher setPage={setPage} />}
      </ButtonsWrapper>
    </SearchResult>
  );
};

export default SearchResults;

const LoadingResultsCount = styled(AnimatedSkeleton)`
  max-width: 350px;
`;

const SearchResult = styled('div')`
  margin-bottom: ${MIDDLE_MARGIN_PX};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Text = styled(Typography)`
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  color: ${({ theme }) => alpha(theme.palette.common.white, 0.6)};
  overflow-wrap: anywhere;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;
const ButtonsWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-left: ${SMALL_MARGIN_PX};
`;
const FilterBtn = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'withToggle',
})<{ withToggle: boolean }>`
  color: ${({ theme }) => theme.palette.common.gray};
  text-transform: uppercase;
  font-size: 16px;
  font-weight: bold;
  padding: 8px 18px;
  border-radius: 50px;
  background: ${({ theme }) => theme.palette.common.card.background.lightInverted};

  &:hover {
    background: ${({ theme }) => theme.palette.common.card.background.lightInverted};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 14px 24px;
    margin-right: ${({ withToggle }) => withToggle && SMALL_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;
const StyledFilterIcon = styled(FilterIcon)`
  display: block;
  width: 16px;
  height: 15px;
  margin-right: 6px;
`;
